// src/components/DeepFikaSignup.jsx
// Deep Fika — 1-on-1 matched fika application form
// Route: /deep-fika/apply
// Submits to: curated-fika-application edge function with format_type: 'deep_fika'

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import curatedFikaAdminService from '../services/curatedFikaAdminService';

// ── SEO config ────────────────────────────────────────────────────────────
export const deepFikaSignupSeoConfig = {
  title: "Apply for Deep Fika — 1-on-1 Matched Fika | Connect & Co",
  description: "Apply for a Deep Fika — a 1-on-1 matched conversation with one person, carefully chosen for you. For active ConnectCo members in Tokyo and Osaka.",
  url: "https://connectco.app/deep-fika/apply",
  canonical: "https://connectco.app/deep-fika/apply",
  robots: "noindex, nofollow",
};

// ── Form options ──────────────────────────────────────────────────────────
const ageRanges = ['21–25', '26–30', '31–35', '36–40', '41–45', '46–50', '51–60', '60+'];

const interestOptions = [
  'Technology & Startups', 'Arts & Culture', 'Travel & Adventure',
  'Food & Culinary', 'Health & Wellness', 'Books & Literature',
  'Music', 'Sports & Fitness', 'Sustainability', 'Personal Development',
  'Photography', 'Languages & Linguistics', 'Film & Cinema',
  'Philosophy & Ideas', 'Parenting', 'Design & Creativity',
];

const conversationTopics = [
  'Life direction & big questions', 'Work and what it means',
  'Culture — being a foreigner in Japan', 'Creativity and making things',
  'Relationships and friendship', 'Mental health and emotional growth',
  'Travel and where life has taken you', 'Current events and the world',
  'Dreams, fears, what keeps you up at night', 'Anything — I go where the conversation leads',
];

const deepFikaGoals = [
  'One genuine friendship', 'Someone who really listens',
  'A person outside my existing social circle', 'Practice being more open',
  'Cultural exchange — share my world and hear someone else\'s',
  'Perspective I wouldn\'t get in my usual life',
  'I\'m not sure — I just want to show up and see what happens',
];

const communicationStyles = [
  'Deep and reflective — I prefer slow, thoughtful conversation',
  'Warm and easy — I like it to feel natural, not intense',
  'Goes anywhere — I\'m comfortable with silence and with being challenged',
  'Flexible — I adapt to whoever I\'m with',
];

const scheduleOptions = [
  'Weekday morning', 'Weekday afternoon', 'Weekday evening',
  'Weekend morning', 'Weekend afternoon', 'Weekend evening', 'Flexible',
];

const locationOptions = [
  'Ginza & Marunouchi', 'Minato (Azabu, Hiroo, Roppongi)',
  'Nakameguro & Daikanyama', 'Omotesando & Aoyama',
  'Shibuya & Harajuku', 'Shinjuku', 'Setagaya', 'Flexible — anywhere in Tokyo',
];

const cityOptions = ['Tokyo', 'Osaka'];
const languageOptions = ['English', 'Japanese', 'French', 'Spanish', 'Portuguese', 'German', 'Korean', 'Mandarin', 'Other'];

// ── Step metadata ─────────────────────────────────────────────────────────
const STEPS = [
  { number: 1, label: 'About You' },
  { number: 2, label: 'Your World' },
  { number: 3, label: 'The Match' },
];

// ── Component ─────────────────────────────────────────────────────────────
const DeepFikaSignup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    // Step 1
    firstName: '',
    lastName: '',
    email: '',
    ageRange: '',
    nationality: '',
    city: 'Tokyo',
    contactInfo: { whatsapp: '', lineId: '', phone: '', preferredMethod: '' },
    // Step 2
    profession: '',
    industry: '',
    yearsInCity: '',
    languages: [],
    interests: [],
    // Step 3
    fikaGoals: [],
    conversationTopics: [],
    communicationStyle: '',
    idealPerson: '',
    meaningfulFika: '',
    schedulePreference: '',
    locationPreference: 'Flexible — anywhere in Tokyo',
    additionalInfo: '',
    friendshipAgreement: false,
    // Hidden fields
    format_type: 'deep_fika',
  });

  const [state, setState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    requiresSubscription: false,
    isResubmission: false,
    error: null,
    message: '',
  });

  const [existingApplication, setExistingApplication] = useState(null);
  const [showLoadPrompt, setShowLoadPrompt] = useState(false);

  // Check for an existing Deep Fika application when email is filled in
  const checkExistingApplication = async (email) => {
    if (!email || !email.includes('@')) return;
    try {
      const result = await curatedFikaAdminService.getApplicationByEmail(email, 'deep_fika');
      if (result?.data?.exists || result?.exists) {
        const appData = result?.data?.data || result?.data;
        setExistingApplication(appData);
        setShowLoadPrompt(true);
      } else {
        setExistingApplication(null);
        setShowLoadPrompt(false);
      }
    } catch (error) {
      console.error('Error checking Deep Fika email:', error);
    }
  };

  // Pre-fill the form with existing application data
  const loadExistingApplication = () => {
    if (!existingApplication) return;
    setFormData(prev => ({
      ...prev,
      firstName: existingApplication.first_name || '',
      lastName: existingApplication.last_name || '',
      email: existingApplication.email || '',
      ageRange: existingApplication.age_range || '',
      nationality: existingApplication.nationality || '',
      city: existingApplication.city || 'Tokyo',
      contactInfo: existingApplication.contact_info || { whatsapp: '', lineId: '', phone: '', preferredMethod: '' },
      profession: existingApplication.profession || '',
      industry: existingApplication.industry || '',
      languages: existingApplication.languages_spoken || [],
      interests: existingApplication.interests || [],
      fikaGoals: existingApplication.fika_goals || [],
      communicationStyle: existingApplication.communication_style || '',
      schedulePreference: existingApplication.schedule_preference || '',
      locationPreference: existingApplication.location_preference || 'Flexible — anywhere in Tokyo',
      additionalInfo: existingApplication.additional_info || '',
      friendshipAgreement: true, // they agreed on first submit
      format_type: 'deep_fika',
    }));
    setShowLoadPrompt(false);
  };

  // Save progress to localStorage
  useEffect(() => {
    const saved = localStorage.getItem('deep_fika_signup_progress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.email) {
          // Only restore if it looks recent (has email)
          setFormData(prev => ({ ...prev, ...parsed, format_type: 'deep_fika' }));
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (formData.email || formData.firstName) {
      localStorage.setItem('deep_fika_signup_progress', JSON.stringify(formData));
    }
  }, [formData]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleArray = (field, value) => setFormData(prev => ({
    ...prev,
    [field]: prev[field].includes(value)
      ? prev[field].filter(v => v !== value)
      : [...prev[field], value],
  }));

  const setContact = (field, value) => setFormData(prev => ({
    ...prev,
    contactInfo: { ...prev.contactInfo, [field]: value },
  }));

  const validate = (step) => {
    switch (step) {
      case 1: {
        const hasContact = formData.contactInfo.whatsapp.trim() ||
          formData.contactInfo.lineId.trim() ||
          formData.contactInfo.phone.trim();
        return formData.firstName.trim() && formData.lastName.trim() &&
          formData.email.trim() && formData.ageRange && hasContact;
      }
      case 2:
        return formData.profession.trim() && formData.interests.length > 0;
      case 3:
        return formData.fikaGoals.length > 0 &&
          formData.communicationStyle &&
          formData.schedulePreference &&
          formData.friendshipAgreement;
      default:
        return false;
    }
  };

  const next = (e) => {
    e.preventDefault();
    if (validate(currentStep) && currentStep < totalSteps) {
      setCurrentStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const back = (e) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(s => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state.isSubmitting || state.isSubmitted) return;
    if (!validate(3)) return;

    setState(s => ({ ...s, isSubmitting: true, error: null }));

    try {
      const payload = {
        ...formData,
        first_name: formData.firstName,
        last_name: formData.lastName,
        age_range: formData.ageRange,
        communication_style: formData.communicationStyle,
        schedule_preference: formData.schedulePreference,
        location_preference: formData.locationPreference,
        languages_spoken: formData.languages,
        fika_goals: formData.fikaGoals,
        format_type: 'deep_fika',
        additional_info: [
          formData.additionalInfo,
          formData.idealPerson ? 'Ideal match: ' + formData.idealPerson : '',
          formData.meaningfulFika ? 'Meaningful fika means: ' + formData.meaningfulFika : '',
          'Conversation topics: ' + formData.conversationTopics.join(', '),
        ].filter(Boolean).join(' | '),
      };

      const response = await fetch(
        import.meta.env.VITE_SUPABASE_URL + '/functions/v1/curated-fika-application',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.success) {
        localStorage.removeItem('deep_fika_signup_progress');
        if (typeof gtag !== 'undefined') {
          gtag('event', result.duplicate ? 'deep_fika_application_updated' : 'deep_fika_application_submitted', {
            event_category: 'conversion',
            city: formData.city,
          });
        }
        setState({ isSubmitting: false, isSubmitted: true, requiresSubscription: result.requiresSubscription || false, isResubmission: result.duplicate || false, error: null, message: result.message || '' });
      } else {
        setState(s => ({ ...s, isSubmitting: false, error: result.error || 'Something went wrong. Please try again.' }));
      }
    } catch (err) {
      setState(s => ({ ...s, isSubmitting: false, error: 'Network error. Please try again.' }));
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────

  if (state.isSubmitted) {
    if (state.requiresSubscription) {
      return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 pt-32 pb-16">
          <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-stone-100">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">💛</span>
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-3">Application saved</h1>
            <p className="text-stone-500 mb-6 leading-relaxed">
              Your Deep Fika application is saved. To activate it and get matched,
              you'll need an active ConnectCo membership.
            </p>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-6 text-left">
              <p className="text-sm font-semibold text-amber-900 mb-3">What happens next:</p>
              <ol className="space-y-2 text-sm text-amber-800">
                <li className="flex gap-2"><span className="font-bold">1.</span> Subscribe to activate your application</li>
                <li className="flex gap-2"><span className="font-bold">2.</span> We review and find your match (1–2 weeks)</li>
                <li className="flex gap-2"><span className="font-bold">3.</span> Introduction email sent to both of you</li>
                <li className="flex gap-2"><span className="font-bold">4.</span> You two arrange the details and meet</li>
              </ol>
            </div>
            <a
              href={'/subscription?email=' + encodeURIComponent(formData.email) + '&source=deep-fika-application'}
              className="block w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-6 rounded-full font-bold hover:from-emerald-700 hover:to-teal-700 transition-all mb-4 text-center"
            >
              Subscribe to activate →
            </a>
            <button
              onClick={() => navigate('/events')}
              className="text-sm text-stone-400 hover:text-stone-600 transition-colors"
            >
              Browse upcoming events in the meantime
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 pt-32 pb-16">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-stone-100">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-3">
            {state.isResubmission ? 'Application updated' : 'Application submitted'}
          </h1>
          <p className="text-stone-500 mb-8 leading-relaxed">
            {state.isResubmission
              ? "Your Deep Fika application has been updated. We'll use your latest details when finding your match."
              : "We have your application. We'll review it and find your match. Expect to hear from us within 1–2 weeks — and when you do, it'll be worth the wait."
            }
          </p>
          <div className="bg-stone-50 rounded-xl border border-stone-100 p-5 mb-8 text-left space-y-2">
            {[
              'Check your email for a confirmation',
              "We'll notify you when your match is ready",
              'You can come to Signature Fika or Monthly Fika in the meantime',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                <p className="text-stone-600 text-sm">{item}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/events')}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-full font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all text-sm"
            >
              Browse events
            </button>
            <button
              onClick={() => navigate('/deep-fika')}
              className="flex-1 border border-stone-200 text-stone-600 py-3 rounded-full font-semibold hover:border-stone-300 transition-all text-sm"
            >
              Back to Deep Fika
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────

  const isStepValid = validate(currentStep);

  return (
    <div className="min-h-screen bg-stone-50">

      {/* Header strip */}
      <div className="pt-24 pb-10 bg-gradient-to-br from-stone-900 to-emerald-950 px-6">
        <div className="max-w-lg mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-medium mb-4">
            <SparklesIcon className="w-4 h-4" />
            Deep Fika Application
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Tell us about yourself
          </h1>
          <p className="text-stone-400 text-sm">
            The more honestly you answer, the better we can match you.
            Takes about 10 minutes.
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b border-stone-100 px-6 py-4 sticky top-16 z-30">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.number}>
                <div className="flex items-center gap-2">
                  <div className={'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ' + (currentStep > step.number ? 'bg-emerald-600 text-white' : currentStep === step.number ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500' : 'bg-stone-100 text-stone-400')}>
                    {currentStep > step.number ? '✓' : step.number}
                  </div>
                  <span className={'text-xs font-medium hidden sm:block ' + (currentStep === step.number ? 'text-stone-900' : 'text-stone-400')}>
                    {step.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={'flex-1 h-0.5 mx-3 ' + (currentStep > step.number ? 'bg-emerald-500' : 'bg-stone-200')} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form body */}
      <div className="max-w-lg mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.form
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            onSubmit={currentStep < totalSteps ? next : submit}
          >

            {/* ── STEP 1: About You ─────────────────────────────────── */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900 mb-1">About you</h2>
                  <p className="text-stone-500 text-sm">Basic details so we can reach you when your match is ready.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">First name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={e => set('firstName', e.target.value)}
                      placeholder="Maya"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Last name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={e => set('lastName', e.target.value)}
                      placeholder="Tanaka"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Email address *
                    <span className="text-stone-400 font-normal ml-2">(we'll check if you've applied before)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => set('email', e.target.value)}
                    onBlur={e => checkExistingApplication(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                    required
                  />
                  <p className="text-xs text-stone-400 mt-1">Must match your ConnectCo membership email.</p>

                  {showLoadPrompt && existingApplication && (
                    <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-emerald-500 mt-0.5 flex-shrink-0">🫂</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-emerald-900">We found your Deep Fika application</p>
                          <p className="text-xs text-emerald-700 mt-0.5">
                            Submitted on {new Date(existingApplication.submitted_at).toLocaleDateString()}
                            {existingApplication.application_status && (
                              <span className="ml-2 capitalize">· Status: {existingApplication.application_status}</span>
                            )}
                          </p>
                          <div className="flex gap-2 mt-3">
                            <button
                              type="button"
                              onClick={loadExistingApplication}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                            >
                              Load my application
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowLoadPrompt(false)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
                            >
                              Start fresh
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Age range *</label>
                  <div className="flex flex-wrap gap-2">
                    {ageRanges.map(age => (
                      <button
                        key={age}
                        type="button"
                        onClick={() => set('ageRange', age)}
                        className={'px-4 py-2 rounded-full text-sm border transition-all ' + (formData.ageRange === age ? 'bg-emerald-600 text-white border-emerald-600' : 'border-stone-200 text-stone-600 hover:border-emerald-400')}
                      >
                        {age}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Nationality / background</label>
                  <input
                    type="text"
                    value={formData.nationality}
                    onChange={e => set('nationality', e.target.value)}
                    placeholder="e.g. American, Japanese, French-Vietnamese..."
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">City *</label>
                  <div className="flex gap-3">
                    {cityOptions.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => set('city', c)}
                        className={'flex-1 py-3 rounded-xl text-sm font-medium border transition-all ' + (formData.city === c ? 'bg-emerald-600 text-white border-emerald-600' : 'border-stone-200 text-stone-600 hover:border-emerald-400')}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Contact method — how should your match reach you? * <span className="text-stone-400 font-normal">(at least one)</span>
                  </label>
                  <div className="space-y-3">
                    {[
                      { key: 'whatsapp', placeholder: 'WhatsApp number (e.g. +1 555 000 0000)', label: 'WhatsApp' },
                      { key: 'lineId', placeholder: 'LINE ID', label: 'LINE' },
                      { key: 'phone', placeholder: 'Phone / other', label: 'Phone' },
                    ].map(({ key, placeholder, label }) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="w-16 text-xs text-stone-500 font-medium">{label}</span>
                        <input
                          type="text"
                          value={formData.contactInfo[key]}
                          onChange={e => setContact(key, e.target.value)}
                          placeholder={placeholder}
                          className="flex-1 border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-2">
                    Contact details are only shared with your matched person, never publicly.
                  </p>
                </div>
              </div>
            )}

            {/* ── STEP 2: Your World ────────────────────────────────── */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900 mb-1">Your world</h2>
                  <p className="text-stone-500 text-sm">What you do, what you care about, what you'd talk about for hours if someone let you.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">What do you do? *</label>
                  <input
                    type="text"
                    value={formData.profession}
                    onChange={e => set('profession', e.target.value)}
                    placeholder="e.g. UX designer at a startup, freelance translator, high school teacher..."
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                    required
                  />
                  <p className="text-xs text-stone-400 mt-1">Be specific — it helps us understand your daily world.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">How long have you been in {formData.city}?</label>
                  <input
                    type="text"
                    value={formData.yearsInCity}
                    onChange={e => set('yearsInCity', e.target.value)}
                    placeholder="e.g. 3 months, 2 years, born here..."
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Languages you speak</label>
                  <div className="flex flex-wrap gap-2">
                    {languageOptions.map(lang => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleArray('languages', lang)}
                        className={'px-4 py-2 rounded-full text-sm border transition-all ' + (formData.languages.includes(lang) ? 'bg-emerald-600 text-white border-emerald-600' : 'border-stone-200 text-stone-600 hover:border-emerald-400')}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    What genuinely interests you? * <span className="text-stone-400 font-normal">Select all that apply</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {interestOptions.map(interest => (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => toggleArray('interests', interest)}
                        className={'px-3 py-2 rounded-full text-xs border transition-all ' + (formData.interests.includes(interest) ? 'bg-emerald-600 text-white border-emerald-600' : 'border-stone-200 text-stone-600 hover:border-emerald-400')}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                  {formData.interests.length === 0 && (
                    <p className="text-xs text-red-400 mt-1">Select at least one.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    What kinds of conversations pull you in? <span className="text-stone-400 font-normal">Select all that feel right</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {conversationTopics.map(topic => (
                      <button
                        key={topic}
                        type="button"
                        onClick={() => toggleArray('conversationTopics', topic)}
                        className={'px-3 py-2 rounded-xl text-xs border transition-all ' + (formData.conversationTopics.includes(topic) ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-200 text-stone-600 hover:border-stone-400')}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: The Match ─────────────────────────────────── */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900 mb-1">The match</h2>
                  <p className="text-stone-500 text-sm">This is the most important part. Help us understand what you're actually looking for.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    What are you hoping to get from this? * <span className="text-stone-400 font-normal">Choose all that feel honest</span>
                  </label>
                  <div className="space-y-2">
                    {deepFikaGoals.map(goal => (
                      <button
                        key={goal}
                        type="button"
                        onClick={() => toggleArray('fikaGoals', goal)}
                        className={'w-full text-left px-4 py-3 rounded-xl text-sm border transition-all ' + (formData.fikaGoals.includes(goal) ? 'bg-emerald-50 border-emerald-400 text-emerald-900 font-medium' : 'border-stone-200 text-stone-600 hover:border-stone-300')}
                      >
                        {goal}
                      </button>
                    ))}
                  </div>
                  {formData.fikaGoals.length === 0 && (
                    <p className="text-xs text-red-400 mt-1">Select at least one.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    How would you describe your communication style? *
                  </label>
                  <div className="space-y-2">
                    {communicationStyles.map(style => (
                      <button
                        key={style}
                        type="button"
                        onClick={() => set('communicationStyle', style)}
                        className={'w-full text-left px-4 py-3 rounded-xl text-sm border transition-all ' + (formData.communicationStyle === style ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-200 text-stone-600 hover:border-stone-300')}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    What kind of person are you hoping to meet?
                  </label>
                  <textarea
                    value={formData.idealPerson}
                    onChange={e => set('idealPerson', e.target.value)}
                    placeholder="Not job titles — more like: someone who reads a lot, or has lived in three different countries, or thinks about the same things I do but from a completely different angle..."
                    rows={3}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white resize-none"
                  />
                  <p className="text-xs text-stone-400 mt-1">Optional, but the more specific you are here, the better we can match.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Best times to meet *
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {scheduleOptions.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => set('schedulePreference', opt)}
                        className={'px-4 py-2 rounded-full text-sm border transition-all ' + (formData.schedulePreference === opt ? 'bg-emerald-600 text-white border-emerald-600' : 'border-stone-200 text-stone-600 hover:border-emerald-400')}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Preferred area in {formData.city}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {locationOptions.map(loc => (
                      <button
                        key={loc}
                        type="button"
                        onClick={() => set('locationPreference', loc)}
                        className={'px-3 py-2 rounded-full text-xs border transition-all ' + (formData.locationPreference === loc ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-200 text-stone-600 hover:border-stone-400')}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Anything else we should know?
                  </label>
                  <textarea
                    value={formData.additionalInfo}
                    onChange={e => set('additionalInfo', e.target.value)}
                    placeholder="Anything that would help us make a better match..."
                    rows={3}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white resize-none"
                  />
                </div>

                {/* Friendship agreement — required */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.friendshipAgreement}
                      onChange={e => set('friendshipAgreement', e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-emerald-600 flex-shrink-0"
                    />
                    <span className="text-sm text-amber-900 leading-relaxed">
                      <strong>I understand that Deep Fika is friendship-only.</strong> I am not applying with
                      romantic intent, business motives, or any hidden agenda. I will treat my match
                      with honesty and respect, in line with{' '}
                      <a href="/community-guidelines" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-700">
                        ConnectCo community guidelines
                      </a>.
                    </span>
                  </label>
                  {!formData.friendshipAgreement && (
                    <p className="text-xs text-amber-700 mt-2 ml-7">Required to submit.</p>
                  )}
                </div>

                {state.error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-red-700 text-sm">{state.error}</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Navigation ─────────────────────────────────────────── */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-stone-100">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors text-sm font-medium"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/deep-fika')}
                  className="text-stone-400 hover:text-stone-600 text-sm transition-colors"
                >
                  ← About Deep Fika
                </button>
              )}

              <button
                type="submit"
                disabled={!isStepValid || state.isSubmitting}
                className={'inline-flex items-center gap-2 font-bold px-8 py-3 rounded-full transition-all duration-300 text-sm ' + (isStepValid && !state.isSubmitting ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 hover:scale-105 shadow-md' : 'bg-stone-100 text-stone-400 cursor-not-allowed')}
              >
                {state.isSubmitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </>
                ) : currentStep < totalSteps ? (
                  <>
                    Continue
                    <ArrowRightIcon className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    {existingApplication ? 'Update application' : 'Submit application'}
                    <ArrowRightIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-xs text-stone-400 mt-4">
              Step {currentStep} of {totalSteps}
            </p>

          </motion.form>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DeepFikaSignup;
